import React from 'react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Trophy, Lightbulb } from 'lucide-react';

const directionConfig = {
  advancing: { icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-500/10', label: 'Advancing' },
  stalled: { icon: Minus, color: 'text-amber-500', bg: 'bg-amber-500/10', label: 'Stalled' },
  regressing: { icon: TrendingDown, color: 'text-red-500', bg: 'bg-red-500/10', label: 'Regressing' },
  completed: { icon: Trophy, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Completed' },
  newly_tracked: { icon: Lightbulb, color: 'text-violet-500', bg: 'bg-violet-500/10', label: 'New' },
  no_longer_tracked: { icon: Minus, color: 'text-gray-400', bg: 'bg-gray-400/10', label: 'Removed' },
};

const severityConfig = {
  critical: 'text-red-500 bg-red-500/10 border-red-500/20',
  high: 'text-orange-500 bg-orange-500/10 border-orange-500/20',
  medium: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
  low: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
};

export default function InsightsSummaryCard({ insights }) {
  if (!insights) {
    return (
      <Card className="rounded-[24px] p-6 glass-subtle">
        <p className="text-sm text-muted-foreground text-center">No insights yet</p>
      </Card>
    );
  }

  const { progress_summary, goal_updates = [], new_risks = [], achievements = [], recommendations = [] } = insights;

  return (
    <div className="space-y-4">
      {/* Progress Summary */}
      {progress_summary && (
        <Card className="rounded-[24px] p-6 glass-elevated">
          <p className="text-sm leading-relaxed text-foreground/90">{progress_summary}</p>
        </Card>
      )}

      {/* Goal Updates */}
      {goal_updates.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground px-1">Goal Progress</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {goal_updates.map((goal) => {
              const config = directionConfig[goal.direction] || directionConfig.stalled;
              const DirIcon = config.icon;
              return (
                <Card key={goal.goal_id} className={cn('rounded-[20px] p-4 glass', config.bg)}>
                  <div className="flex items-start gap-3">
                    <div className={cn('mt-0.5 rounded-full p-1.5', config.bg)}>
                      <DirIcon className={cn('w-4 h-4', config.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-semibold truncate">{goal.title}</h5>
                      <div className="flex items-center gap-2 mt-1">
                        {goal.previous_progress != null && (
                          <span className="text-xs text-muted-foreground">{goal.previous_progress}%</span>
                        )}
                        {goal.previous_progress != null && (
                          <span className="text-xs text-muted-foreground">&rarr;</span>
                        )}
                        <span className={cn('text-xs font-medium', config.color)}>
                          {goal.current_progress}%
                        </span>
                        <span className={cn('text-xs px-1.5 py-0.5 rounded-full', config.bg, config.color)}>
                          {config.label}
                        </span>
                      </div>
                      {goal.assessment && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{goal.assessment}</p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Achievements */}
      {achievements.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground px-1">Achievements</h4>
          <div className="space-y-2">
            {achievements.map((achievement, i) => (
              <Card key={i} className="rounded-[20px] p-4 glass bg-amber-500/5">
                <div className="flex items-start gap-3">
                  <Trophy className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">{achievement.title}</p>
                    {achievement.detail && (
                      <p className="text-xs text-muted-foreground mt-0.5">{achievement.detail}</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Risks */}
      {new_risks.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground px-1">New Risks</h4>
          <div className="space-y-2">
            {new_risks.map((risk, i) => (
              <Card
                key={i}
                className={cn(
                  'rounded-[20px] p-4 glass border',
                  severityConfig[risk.severity] || severityConfig.medium
                )}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{risk.title}</p>
                      <span className="text-[10px] uppercase font-bold tracking-wider opacity-70">
                        {risk.severity}
                      </span>
                    </div>
                    {risk.context && (
                      <p className="text-xs text-muted-foreground mt-0.5">{risk.context}</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground px-1">Recommendations</h4>
          <Card className="rounded-[20px] p-4 glass">
            <ul className="space-y-2">
              {recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Lightbulb className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground/90">{rec}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}
